import { useEffect, useReducer } from 'react';

import { CharacterFace, usePhase } from './CharacterFace';
import { clock } from './clock';
import { expressionFor } from './expression';
import { heldExpression, holdRemaining } from './reaction';
import { t, type PlainKey } from '../i18n/t';

/** §11: every avatar state has a text equivalent, and it is a live region. */
const SAYS: Partial<Record<string, PlainKey>> = {
  neutral: 'avatar-waiting',
  listening: 'avatar-listening',
  thinking: 'avatar-thinking',
  speaking: 'avatar-speaking',
  concern: 'avatar-concern',
  celebrate: 'avatar-celebrate',
};

/**
 * The character's face, tracking the turn.
 *
 * The drawing is `CharacterFace`; what this adds is the turn — which
 * expression the state and the latest reaction cue add up to, and the live
 * region that says it in words.
 */
export function TutorAvatar({
  artKey,
  displayName,
  accentHex,
  intensity,
  state,
  cue,
  cueAt,
  onHearAgain,
  replaying,
}: {
  artKey: string;
  displayName: string;
  accentHex: string;
  intensity: number;
  state: string;
  /// The most recent `ReactionCue`, or null. Held briefly over the turn state.
  cue: string | null;
  /// When it arrived, on the same timebase as the clock: `performance.now()`.
  cueAt: number;
  /// Present while a tap can replay the last line, or one is replaying: the
  /// face is then a button (hear-again design §3).
  onHearAgain?: () => void;
  /// A replay is playing, so the face speaks although the server is idle.
  replaying?: boolean;
}) {
  const phase = usePhase();
  // Under reduced motion the phase is pinned at 0, so a cue's hold is timed
  // off the wall clock instead — PRD §11: expressions still change — and a
  // timer re-renders when it ends, since no frame will.
  const reduced = clock?.reduced() ?? false;
  const now = reduced ? performance.now() : phase;
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!reduced) return;
    const left = holdRemaining(cue, cueAt, performance.now());
    if (left === null) return;
    const id = setTimeout(rerender, left + 1);
    return () => clearTimeout(id);
  }, [reduced, cue, cueAt]);
  // A cue answers the turn the learner just took; the turn state says where
  // the loop is. The cue wins while it is fresh, which is what makes it read
  // as a reaction rather than as another state.
  const expression = heldExpression(cue, cueAt, now, expressionFor(replaying ? 'speaking' : state));
  const says = SAYS[expression];

  const face = (
    <CharacterFace artKey={artKey} accentHex={accentHex} intensity={intensity} expression={expression} phase={phase} />
  );

  return (
    <div className="flex flex-col items-center gap-1">
      {onHearAgain ? (
        <button
          type="button"
          className="w-full max-w-[12rem] cursor-pointer rounded-full"
          aria-label={t('session-hear-again')}
          onClick={onHearAgain}
        >
          {face}
        </button>
      ) : (
        <div className="w-full max-w-[12rem]">{face}</div>
      )}
      {/* The state must never be carried by motion alone (§11). This is the
          text equivalent, and it is the only live region — two of them
          announcing the same change is worse than none. */}
      <p aria-live="polite" className="text-base-content/60 text-sm">
        {t('avatar-says', { name: displayName, state: says ? t(says) : '' })}
      </p>
    </div>
  );
}
